import { motion } from "motion/react";
import { Link } from "react-router-dom";
import {
  Settings,
  Brain,
  Shield,
  Code,
  ArrowRight,
} from "lucide-react";
import { Card } from "../ui/card";
import { Button } from "../ui/button";

interface SolutionTileProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  features: string[];
  link: string;
  delay?: number;
}

function SolutionTile({
  icon,
  title,
  description,
  features,
  link,
  delay = 0,
}: SolutionTileProps) {
  // Create ID from title for section linking
  const sectionId = title.toLowerCase().replace(/\s+/g, "-");

  return (
    <motion.div
      id={sectionId}
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay }}
      whileHover={{ y: -10, rotateX: 5 }}
      className="perspective-1000 scroll-mt-20"
    >
      <Card className="glass p-8 h-full group hover:border-primary/50 transition-all duration-500 relative overflow-hidden">
        {/* Background glow */}
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 rounded-lg"
          whileHover={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
        />

        <div className="relative z-10">
          {/* Icon */}
          <motion.div
            className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center mb-6 text-black"
            whileHover={{ scale: 1.1, rotate: 5 }}
            transition={{ duration: 0.3 }}
          >
            {icon}
          </motion.div>

          {/* Content */}
          <h3 className="text-2xl font-bold mb-4 group-hover:text-primary transition-colors">
            {title}
          </h3>

          <p className="text-muted-foreground mb-6 leading-relaxed">
            {description}
          </p>

          {/* Features */}
          <ul className="space-y-2 mb-8">
            {features.map((feature, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{
                  duration: 0.5,
                  delay: delay + 0.1 * (index + 1),
                }}
                className="flex items-center text-sm text-muted-foreground"
              >
                <div className="w-1.5 h-1.5 bg-accent rounded-full mr-3" />
                {feature}
              </motion.li>
            ))}
          </ul>

          {/* Link */}
          <Link to={link}>
            <Button
              variant="outline"
              className="w-full rounded-full border-primary/50 hover:border-primary hover:bg-primary/10 group/btn"
            >
              Learn More
              <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>

        {/* Decorative elements */}
        <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-accent/10 to-transparent rounded-full blur-xl" />
        <div className="absolute bottom-4 left-4 w-16 h-16 bg-gradient-to-br from-primary/10 to-transparent rounded-full blur-lg" />
      </Card>
    </motion.div>
  );
}

export function SolutionsPage() {
  const solutions = [
    {
      icon: <Settings className="w-8 h-8" />,
      title: "Operational Intelligence & Automation",
      description:
        "We integrate IoT, AI, and advanced analytics to optimize operations, eliminate inefficiencies, and enable real-time, data-driven decisions.",
      features: [
        "Real-time sensor integration with anomaly detection",
        "Automated alerts, dashboards, and seamless data flow",
        "Digital tools for task tracking, HR automation, and centralized visibility",
        "Predictive maintenance and workflow optimization",
      ],
      link: "/solutions/operational-intelligence-automation",
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI-Powered Decision Making",
      description:
        "We make data clear, accessible, and actionable—turning massive datasets into strategic insights for informed business decisions.",
      features: [
        "NLP-powered summarization & sentiment analysis",
        "Automated classification, trend detection, and behavior insights",
        "AI-driven recommendations aligned with business goals",
        "Advanced data visualization and predictive analytics",
      ],
      link: "/solutions/ai-powered-decision-making",
    },
    {
      icon: <Shield className="w-8 h-8" />,
      title: "Workplace Safety & Compliance",
      description:
        "AI-driven systems for proactive safety monitoring and simplified regulatory compliance to ensure secure work environments.",
      features: [
        "Visual recognition of PPE and immediate breach alerts",
        "Automated DGE documentation and compliance tracking",
        "Scalable solutions for diverse worksite environments",
        "Real-time safety monitoring and incident detection",
      ],
      link: "/solutions/workplace-safety-compliance",
    },
    {
      icon: <Code className="w-8 h-8" />,
      title: "Digital Transformation & IT Enablement",
      description:
        "Custom software and web solutions that are scalable, secure, and aligned with business objectives to drive digital transformation.",
      features: [
        "Custom dashboards, portals, and integrations",
        "Responsive, SEO-optimized, and high-performance websites",
        "Seamless cross-device accessibility",
        "Cloud-native architecture and enterprise-grade security",
      ],
      link: "/solutions/digital-transformation-it-enablement",
    },
  ];

  

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="py-5 bg-gradient-to-b from-background to-background/50 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,${encodeURIComponent('<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#00cfff" fill-opacity="0.03"><path d="M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z"/></g></g></svg>')}")`,
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6 neon-text">
              <span className="amasqis-text">amasQIS.ai's</span> Solutions
            </h1>
            <p className="text-xl md:text-2xl text-center text-muted-foreground mb-8">
              Comprehensive AI-powered solutions designed to transform your operations, enhance decision-making, ensure compliance, and drive digital transformation across industries.
            </p>

            {/* Animated code snippet */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="glass p-6 rounded-lg text-left max-w-2xl mx-auto text-sm font-mono"
            >
              <div className="text-primary mb-2">
                # Initialize amasQIS AI Solutions
              </div>
              <div className="text-muted-foreground">
                <span className="text-accent">from</span>{" "}
                amasqis{" "}
                <span className="text-accent">import</span>{" "}
                SmartOperations, AIDecisions, SafetyCompliance, DigitalTransform
                <br />
                <span className="text-accent">
                  solutions =
                </span>{" "}
                amasqis.transform_business()
                <br />
                <span className="text-accent">
                  results =
                </span>{" "}
                solutions.optimize_everything()
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Our Solution Categories
            </h2>
            <p className="text-xl text-center text-muted-foreground max-w-3xl mx-auto">
              Four comprehensive solution categories designed to address every aspect of modern business transformation and optimization.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
            {solutions.map((solution, index) => (
              <SolutionTile
                key={solution.title}
                {...solution}
                delay={index * 0.2}
              />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}